import React, { useEffect, useLayoutEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Spinner from "react-bootstrap/Spinner";
import { FaBed, FaBath, FaCar, FaRulerCombined, FaEnvelope, FaHome, FaChartLine, FaArrowLeft, FaExternalLinkAlt, FaStickyNote, FaCheck, FaChevronDown, FaChevronUp, FaBell, FaEye, FaRobot, FaTimes, FaTrash, FaClipboardCheck, FaHistory, FaInfoCircle, FaCopy, FaRegCircle, FaCheckCircle, FaArrowUp, FaArrowDown, FaMinus, FaStar, FaCalendarAlt, FaMapMarkerAlt, FaSms, FaPaperclip } from "react-icons/fa";
import { handleTokenAndPostRequest } from "../utils/utils";
import { smsPartCount, smsPartLimits, smsCost, SMS_PRICE_PER_PART } from "../utils/sms";
import BuyExtraUsageModal from "../components/BuyExtraUsageModal";

function formatCurrency(val) {
  if (val == null) return "—";
  const num = Number(val);
  if (isNaN(num)) return typeof val === "string" ? val : "—";
  return "$" + num.toLocaleString("en-AU", { maximumFractionDigits: 0 });
}

function formatDate(dateStr) {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" });
}

function CopyButton({ text }) {
  const [copied, setCopied] = React.useState(false);
  const handleCopy = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <button
      onClick={handleCopy}
      title={copied ? "Copied!" : "Copy"}
      style={{
        background: "none",
        border: "none",
        cursor: "pointer",
        padding: 2,
        display: "inline-flex",
        alignItems: "center",
        color: copied ? "#22c55e" : "#9ca3af",
        fontSize: 12,
        flexShrink: 0,
        transition: "color 0.2s",
      }}
    >
      {copied ? <FaCheck /> : <FaCopy />}
    </button>
  );
}

// Must match the hash function used in PropertySearches.js
export function hashEmail(email) {
  try {
    return btoa(email).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
  } catch {
    return encodeURIComponent(email);
  }
}

function TruncatedAISummary({ html }) {
  const [expanded, setExpanded] = useState(false);
  const MAX_HEIGHT = 120;
  const contentRef = React.useRef(null);
  const [needsTruncation, setNeedsTruncation] = useState(false);
  const measured = React.useRef(false);

  // Measure once after first paint, not on every html change
  React.useLayoutEffect(() => {
    measured.current = false;
  }, [html]);

  const measuredCallback = React.useCallback((node) => {
    contentRef.current = node;
    if (node && !measured.current) {
      measured.current = true;
      setNeedsTruncation(node.scrollHeight > MAX_HEIGHT + 20);
    }
  }, [html]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div>
      <div style={{ position: "relative" }}>
        <div
          ref={measuredCallback}
          style={{
            fontSize: 13,
            lineHeight: 1.7,
            color: "#374151",
            maxHeight: expanded ? 99999 : MAX_HEIGHT,
            overflow: "hidden",
          }}
          dangerouslySetInnerHTML={{ __html: html }}
        />
        {needsTruncation && !expanded && (
          <div style={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            height: 48,
            background: "linear-gradient(transparent, #fff)",
            pointerEvents: "none",
          }} />
        )}
      </div>
      {needsTruncation && (
        <button
          onClick={() => setExpanded(prev => !prev)}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            marginTop: 6,
            padding: 0,
            background: "none",
            border: "none",
            fontSize: 12,
            fontWeight: 600,
            color: "#2563eb",
            cursor: "pointer",
          }}
        >
          {expanded ? "Show less" : "Read full analysis"}
          {expanded
            ? <FaChevronUp style={{ fontSize: 9 }} />
            : <FaChevronDown style={{ fontSize: 9 }} />
          }
        </button>
      )}
    </div>
  );
}

export default function PropertySearchDetail() {
  const { emailHash, propertyId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [rawData, setRawData] = useState([]);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [note, setNote] = useState("");
  const [savedNote, setSavedNote] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [noteSaved, setNoteSaved] = useState(false);
  const [notes, setNotes] = useState([]);
  const [notesLoading, setNotesLoading] = useState(false);
  const [cmas, setCmas] = useState([]);
  const [cmasLoading, setCmasLoading] = useState(false);
  const [uploadingCma, setUploadingCma] = useState(false);
  const [cmaUploadError, setCmaUploadError] = useState("");
  const [deletingCmaId, setDeletingCmaId] = useState(null);
  const cmaFileInputRef = React.useRef(null);
  const [markingComplete, setMarkingComplete] = useState(false);
  const [selectedEntryIndex, setSelectedEntryIndex] = useState(0);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [noteOptions, setNoteOptions] = useState({ notify_email: true, notify_sms: true, visible_to_user: true, visible_to_ai: true });
  const [channels, setChannels] = useState({ has_email: false, has_sms: false, email: null, mobile: null, full_name: null, loaded: false });
  const [deletingNoteId, setDeletingNoteId] = useState(null);
  const [balance, setBalance] = useState(null);
  const [balanceLoading, setBalanceLoading] = useState(true);
  const [showBuyCreditModal, setShowBuyCreditModal] = useState(false);
  const [buyCreditSuggested, setBuyCreditSuggested] = useState(25);
  const [moreDropdownOpen, setMoreDropdownOpen] = useState(false);
  const [activityDropdownOpen, setActivityDropdownOpen] = useState(false);
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiGenerating, setAiGenerating] = useState(false);
  const [compTab, setCompTab] = useState("sale");
  const moreDropdownRef = React.useRef(null);
  const activityDropdownRef = React.useRef(null);
  const noteTextareaRef = React.useRef(null);
  const aiPromptRef = React.useRef(null);

  // Auto-resize textarea to fit content
  useEffect(() => {
    const el = noteTextareaRef.current;
    if (el) {
      el.style.height = "auto";
      el.style.height = Math.max(72, el.scrollHeight) + "px";
    }
  }, [note]);

  // Auto-resize AI prompt textarea
  useLayoutEffect(() => {
    const el = aiPromptRef.current;
    if (el) {
      el.style.height = "0px";
      el.style.height = Math.max(isMobile ? 48 : 36, el.scrollHeight) + "px";
    }
  }, [aiPrompt, isMobile]);

  useEffect(() => {
    function handleResize() { setIsMobile(window.innerWidth < 768); }
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Close More dropdown on click outside
  useEffect(() => {
    function handleClickOutside(e) {
      if (moreDropdownRef.current && !moreDropdownRef.current.contains(e.target)) {
        setMoreDropdownOpen(false);
      }
      if (activityDropdownRef.current && !activityDropdownRef.current.contains(e.target)) {
        setActivityDropdownOpen(false);
      }
    }
    if (moreDropdownOpen || activityDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [moreDropdownOpen, activityDropdownOpen]);

  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      try {
        const user = JSON.parse(localStorage.getItem("user") || "{}");
        const agent_id = user.agent_details?.agent_id;
        const agency_id = user.agent_details?.agency_id;
        const res = await handleTokenAndPostRequest(
          `${window.api_host}/property-report-requests`,
          { agent_id, agency_id }
        );
        setRawData(Array.isArray(res.data) ? res.data : []);
      } catch {
        setRawData([]);
      }
      setLoading(false);
    }
    fetchData();
  }, []);

  // Find all entries for this property_id + email combo
  const { entries, latest } = useMemo(() => {
    const matching = rawData.filter(item => {
      const pid = item.property_id || item.metadata?.property_id || "";
      const email = item.email || "";
      return pid === propertyId && hashEmail(email) === emailHash;
    }).sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

    return { entries: matching, latest: matching[0] || null };
  }, [rawData, propertyId, emailHash]);

  // Load existing note from data
  useEffect(() => {
    if (latest) {
      setNote("");
      setSavedNote("");
    }
  }, [latest]);

  // Fetch the agent's extra-usage credit balance (used to gate SMS sends).
  const fetchBalance = React.useCallback(() => {
    setBalanceLoading(true);
    handleTokenAndPostRequest(`${window.api_host}/billing/balance`)
      .then((res) => setBalance(res.data || null))
      .catch(() => setBalance(null))
      .finally(() => setBalanceLoading(false));
  }, []);
  useEffect(() => { fetchBalance(); }, [fetchBalance]);

  // Fetch which notification channels are available for this contact
  useEffect(() => {
    if (!latest?.email) {
      setChannels({ has_email: false, has_sms: false, email: null, mobile: null, full_name: null, loaded: true });
      return;
    }
    async function fetchChannels() {
      try {
        const res = await handleTokenAndPostRequest(
          `${window.api_host}/get-contact-channels`,
          { user_email: latest.email }
        );
        setChannels({
          has_email: !!res.data?.has_email,
          has_sms: !!res.data?.has_sms,
          email: res.data?.email || null,
          mobile: res.data?.mobile || null,
          full_name: res.data?.full_name || null,
          loaded: true,
        });
      } catch {
        setChannels({ has_email: false, has_sms: false, email: null, mobile: null, full_name: null, loaded: true });
      }
    }
    fetchChannels();
  }, [latest]);

  // Fetch notes history
  useEffect(() => {
    if (!latest) return;
    async function fetchNotes() {
      setNotesLoading(true);
      try {
        const res = await handleTokenAndPostRequest(
          `${window.api_host}/get-notes`,
          { property_id: propertyId, user_email: latest.email, view_scope: 'agency' }
        );
        const list = Array.isArray(res.data) ? res.data : [];
        setNotes(list.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)));
      } catch {
        setNotes([]);
      }
      setNotesLoading(false);
    }
    fetchNotes();
  }, [latest, propertyId]);

  // Fetch CMA documents history
  useEffect(() => {
    if (!latest) return;
    async function fetchCmas() {
      setCmasLoading(true);
      try {
        const res = await handleTokenAndPostRequest(
          `${window.api_host}/list-property-cmas`,
          { property_id: propertyId, user_email: latest.email }
        );
        const list = Array.isArray(res.data) ? res.data : [];
        setCmas(list);
      } catch {
        setCmas([]);
      }
      setCmasLoading(false);
    }
    fetchCmas();
  }, [latest, propertyId]);

  async function handleUploadCma(file) {
    if (!file || !latest) return;
    setCmaUploadError("");
    if (!/\.pdf$/i.test(file.name)) {
      setCmaUploadError("Only PDF files are supported.");
      return;
    }
    if (file.size > 25 * 1024 * 1024) {
      setCmaUploadError("File too large (max 25 MB).");
      return;
    }
    setUploadingCma(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("property_id", propertyId);
      if (latest.email) fd.append("user_email", latest.email);
      const res = await handleTokenAndPostRequest(
        `${window.api_host}/upload-property-cma`,
        fd
      );
      const cma = res.data?.cma;
      if (cma) setCmas(prev => [cma, ...prev]);
    } catch (e) {
      console.error("CMA upload failed", e);
      setCmaUploadError(e?.response?.data?.error || "Upload failed.");
    }
    setUploadingCma(false);
    if (cmaFileInputRef.current) cmaFileInputRef.current.value = "";
  }

  async function handleDeleteCma(id) {
    if (!id) return;
    setDeletingCmaId(id);
    try {
      await handleTokenAndPostRequest(
        `${window.api_host}/delete-property-cma`,
        { id }
      );
      setCmas(prev => prev.filter(c => c.id !== id));
    } catch (e) {
      console.error("CMA delete failed", e);
    }
    setDeletingCmaId(null);
  }

  async function handleAiGenerate() {
    if (!aiPrompt.trim() || aiGenerating) return;
    setAiGenerating(true);
    try {
      const selReq = (entries[selectedEntryIndex] || latest)?.metadata?.request || {};
      const selMeta = (entries[selectedEntryIndex] || latest)?.metadata || {};
      const res = await handleTokenAndPostRequest(
        `${window.api_host}/generate-review-note`,
        {
          prompt: aiPrompt.trim(),
          address: selReq.address_text || "",
          ai_summary: selMeta.ai_summary || "",
          estimate_low: selMeta.estimated_value_low,
          estimate_high: selMeta.estimated_value_high,
        }
      );
      if (res.data?.note) {
        setNote(res.data.note);
        setNoteSaved(false);
      }
    } catch (e) {
      console.error("AI generate failed", e);
    }
    setAiGenerating(false);
  }

  function handleSaveClick() {
    if (!latest || !note.trim()) return;
    setShowNoteModal(true);
  }

  async function handleConfirmSaveNote() {
    if (!latest || !note.trim()) return;
    const noteText = note.trim();
    const willNotifyEmail = channels.has_email && noteOptions.notify_email;
    const willNotifySms = channels.has_sms && noteOptions.notify_sms;

    // Credit check — block the send if SMS is enabled and the agent's
    // extra-usage balance can't cover the cost of all message parts.
    if (willNotifySms) {
      const cost = smsCost(noteText);
      const have = Number(balance?.balance_amount ?? 0);
      if (cost > have + 1e-9) {
        const shortfall = cost - have;
        // Suggest the next preset (10/25/50/100/250) that comfortably covers
        // the shortfall, defaulting to A$25.
        const presets = [10, 25, 50, 100, 250];
        const suggested = presets.find((p) => p >= shortfall) || 25;
        setBuyCreditSuggested(suggested);
        // Hide the note modal while the buy-credit modal is up so the two
        // don't visually stack on top of each other.
        setShowNoteModal(false);
        setShowBuyCreditModal(true);
        return;
      }
    }

    setShowNoteModal(false);
    setSavingNote(true);
    setNoteSaved(false);
    try {
      const res = await handleTokenAndPostRequest(
        `${window.api_host}/save-property-note`,
        {
          property_id: propertyId,
          user_email: latest.email,
          note: noteText,
          notify_email: willNotifyEmail,
          notify_sms: willNotifySms,
          visible_to_user: noteOptions.visible_to_user,
          visible_to_ai: noteOptions.visible_to_ai,
        }
      );
      const resNote = res.data && typeof res.data === "object" ? res.data : null;
      const newNote = {
        id: resNote?.id,
        property_id: propertyId,
        user_email: latest.email,
        note: resNote?.note || noteText,
        notify_user: resNote?.notify_user ?? (willNotifyEmail || willNotifySms),
        visible_to_user: resNote?.visible_to_user ?? noteOptions.visible_to_user,
        visible_to_ai: resNote?.visible_to_ai ?? noteOptions.visible_to_ai,
        created_at: resNote?.created_at || new Date().toISOString(),
      };
      setNotes(prev => [newNote, ...prev]);
      setNote("");
      setSavedNote("");
      setNoteSaved(true);
      setNoteOptions({ notify_email: true, notify_sms: true, visible_to_user: true, visible_to_ai: true });
      setTimeout(() => setNoteSaved(false), 2000);
    } catch (e) {
      console.error("Failed to save note", e);
    }
    setSavingNote(false);
  }

  async function handleDeleteNote(noteId) {
    if (!noteId) return;
    setDeletingNoteId(noteId);
    try {
      await handleTokenAndPostRequest(
        `${window.api_host}/delete-note`,
        { note_id: noteId }
      );
      setNotes(prev => prev.filter(n => n.id !== noteId));
    } catch (e) {
      console.error("Failed to delete note", e);
    }
    setDeletingNoteId(null);
  }

  const req = latest?.metadata?.request || {};
  const meta = latest?.metadata || {};
  const isLegacy = !!meta.legacy_alert;
  const addr = req.address_text || "Unknown Address";
  const rawImg = req.media_urls?.[0] || null;
    const heroImg = rawImg && typeof rawImg === 'object' ? (rawImg.largeImage || rawImg.smallImage) : rawImg;

  // Selected entry for estimates + AI summary
  const selectedEntry = entries[selectedEntryIndex] || latest;
  const selectedMeta = selectedEntry?.metadata || {};
  const selectedReq = selectedEntry?.metadata?.request || {};

  return (
    <div style={{
      padding: isMobile ? "0 16px" : "0 32px",
      flex: 1,
      minWidth: 0,
      display: "flex",
      flexDirection: "column",
      background: "#f6f8fa",
      boxSizing: "border-box",
      width: "100%",
      position: "relative",
      overflow: "auto",
    }}>
      <div style={{ maxWidth: 900, marginLeft: "auto", marginRight: "auto", width: "100%", paddingTop: isMobile ? 72 : 32, paddingBottom: 40 }}>
        {/* Back button */}
        <button
          onClick={() => navigate("/leads-property-searches")}
          style={{
            background: "none",
            border: "none",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: 6,
            fontSize: 14,
            color: "#2563eb",
            fontWeight: 600,
            padding: "0 0 16px 0",
          }}
        >
          <FaArrowLeft style={{ fontSize: 12 }} /> Back to Property Searches
        </button>

        {loading ? (
          <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 400 }}>
            <Spinner animation="border" />
          </div>
        ) : !latest ? (
          <div style={{
            background: "#fff",
            borderRadius: 12,
            padding: 32,
            boxShadow: "0 2px 8px #0001",
            border: "1px solid #e5e7eb",
            textAlign: "center",
          }}>
            <h2 style={{ fontSize: 20, fontWeight: 600, color: "#1f2937", marginBottom: 12 }}>Property Not Found</h2>
            <p style={{ color: "#6b7280", fontSize: 15 }}>This property search could not be found.</p>
          </div>
        ) : (
          <>
            {/* Hero image */}
            <div style={{
              borderRadius: 12,
              overflow: "hidden",
              marginBottom: 20,
              maxHeight: 320,
              background: "#f3f4f6",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: 320,
            }}>
              {heroImg ? (
                <img
                  src={heroImg}
                  alt="Property"
                  onError={e => { e.target.style.display = "none"; e.target.nextSibling.style.display = "flex"; }}
                  style={{ width: "100%", height: 320, objectFit: "cover", display: "block" }}
                />
              ) : null}
              <div style={{ display: heroImg ? "none" : "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", width: "100%", height: "100%", gap: 8 }}>
                <FaHome style={{ fontSize: 48, color: "#d1d5db" }} />
                <span style={{ fontSize: 12, color: "#c0c4cc", fontWeight: 500 }}>No image available</span>
              </div>
            </div>

            {/* Header card */}
            <div style={{
              background: "#fff",
              borderRadius: 12,
              padding: isMobile ? 16 : 24,
              boxShadow: "0 2px 8px #0001",
              border: "1px solid #e5e7eb",
              marginBottom: 16,
            }}>
              {/* Pills */}
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 10, flexWrap: "wrap" }}>
                <span style={{
                  borderRadius: 16, padding: "2px 12px", fontWeight: 600, fontSize: 12,
                  background: req.report_type === "property_report" ? "#eff6ff" : "#f3e8ff",
                  color: req.report_type === "property_report" ? "#2563eb" : "#7c3aed",
                  border: `1px solid ${req.report_type === "property_report" ? "#bfdbfe" : "#c4b5fd"}`,
                }}>
                  {req.report_type === "property_report" ? "Report Download" : "Property Analysis"}
                </span>
                {req.property_type && (
                  <span style={{
                    borderRadius: 16, padding: "2px 12px", fontWeight: 600, fontSize: 12,
                    background: "#e7fbe9", color: "#15803d", border: "1px solid #22c55e",
                  }}>
                    {req.property_type}
                  </span>
                )}
                {entries.length > 1 && (
                  <span style={{
                    borderRadius: 16, padding: "2px 12px", fontWeight: 600, fontSize: 12,
                    background: "#fef3c7", color: "#b45309", border: "1px solid #f59e42",
                  }}>
                    {entries.length} view{entries.length !== 1 ? "s" : ""}
                  </span>
                )}
              </div>

              {/* Address */}
              <h1 style={{ fontSize: isMobile ? 22 : 28, fontWeight: 700, color: "#1f2937", margin: "0 0 10px 0", lineHeight: 1.3, display: "flex", alignItems: "center", gap: 6 }}>
                <span>{addr}</span>
                <CopyButton text={addr} />
              </h1>

              {/* Features */}
              <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 12, fontSize: 14, color: "#374151" }}>
                {req.bedrooms && (
                  <span style={{ display: "flex", alignItems: "center", gap: 5 }}>
                    <FaBed style={{ fontSize: 13, color: "#6b7280" }} /> {req.bedrooms} bed
                  </span>
                )}
                {req.bathrooms && (
                  <span style={{ display: "flex", alignItems: "center", gap: 5 }}>
                    <FaBath style={{ fontSize: 13, color: "#6b7280" }} /> {req.bathrooms} bath
                  </span>
                )}
                {req.carspaces && (
                  <span style={{ display: "flex", alignItems: "center", gap: 5 }}>
                    <FaCar style={{ fontSize: 13, color: "#6b7280" }} /> {req.carspaces} car
                  </span>
                )}
                {req.land_area && (
                  <span style={{ display: "flex", alignItems: "center", gap: 5 }}>
                    <FaRulerCombined style={{ fontSize: 13, color: "#6b7280" }} /> {req.land_area} m²
                  </span>
                )}
              </div>

              {/* Visitor identity */}
              {channels.full_name && (
                <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 14, color: "#1f2937", fontWeight: 600, marginBottom: 6 }}>
                  <span>{channels.full_name}</span>
                </div>
              )}
              {(channels.email || (latest.email && latest.email.includes("@"))) && (
                <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 14, color: "#4b5563", marginBottom: 6 }}>
                  <FaEnvelope style={{ fontSize: 12, color: "#9ca3af" }} />
                  <span style={{ fontWeight: 500 }}>{channels.email || latest.email}</span>
                  <CopyButton text={channels.email || latest.email} />
                </div>
              )}
              {(channels.mobile || (latest.email && !latest.email.includes("@"))) && (
                <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 14, color: "#4b5563", marginBottom: 12 }}>
                  <FaSms style={{ fontSize: 12, color: "#9ca3af" }} />
                  <span style={{ fontWeight: 500 }}>{channels.mobile || latest.email}</span>
                  <CopyButton text={channels.mobile || latest.email} />
                </div>
              )}
              {!channels.full_name && !channels.email && !channels.mobile && !latest.email && (
                <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 14, color: "#4b5563", marginBottom: 12 }}>
                  <FaEnvelope style={{ fontSize: 12, color: "#9ca3af" }} />
                  <span style={{ fontWeight: 500 }}>—</span>
                </div>
              )}

              {/* Date */}
              <div style={{ fontSize: 13, color: "#9ca3af" }}>
                Last viewed: {formatDate(latest.created_at)}
                {entries.length > 1 && (
                  <span style={{ marginLeft: 10 }}>· First viewed: {formatDate(entries[entries.length - 1].created_at)}</span>
                )}
              </div>

              {/* Review status / Mark complete */}
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 10, fontSize: 13 }}>
                <label style={{ display: "flex", alignItems: "center", gap: 5, cursor: markingComplete ? "default" : "pointer", margin: 0 }}>
                  <input
                    type="checkbox"
                    checked={notes.some(n => n.note === "Marked as complete")}
                    disabled={markingComplete}
                    onChange={async (e) => {
                      const isComplete = notes.some(n => n.note === "Marked as complete");
                      setMarkingComplete(true);
                      try {
                        if (isComplete) {
                          const completeNotes = notes.filter(n => n.note === "Marked as complete");
                          for (const cn of completeNotes) {
                            await handleTokenAndPostRequest(`${window.api_host}/delete-note`, { note_id: cn.id });
                          }
                          setNotes(prev => prev.filter(n => n.note !== "Marked as complete"));
                        } else {
                          const res = await handleTokenAndPostRequest(`${window.api_host}/save-property-note`, {
                            property_id: propertyId,
                            user_email: latest.email,
                            note: "Marked as complete",
                            notify_email: false,
                            notify_sms: false,
                            visible_to_user: false,
                            visible_to_ai: false,
                          });
                          const newId = res.data?.id;
                          setNotes(prev => [{ id: newId || 0, note: "Marked as complete", property_id: propertyId, user_email: latest.email, notify_user: false, visible_to_user: false, visible_to_ai: false, created_at: new Date().toISOString() }, ...prev]);
                        }
                      } catch (err) {
                        console.error("Failed to toggle complete:", err);
                      }
                      setMarkingComplete(false);
                    }}
                    style={{ width: 18, height: 18, accentColor: "#22c55e", cursor: markingComplete ? "default" : "pointer" }}
                  />
                  {markingComplete ? (
                    <Spinner animation="border" size="sm" style={{ width: 14, height: 14, color: "#9ca3af" }} />
                  ) : notes.some(n => n.note === "Marked as complete") ? (
                    <span style={{ color: "#15803d", fontWeight: 500 }}>Completed</span>
                  ) : (
                    <span style={{ color: "#9ca3af" }}>Mark as complete</span>
                  )}
                </label>
              </div>

              {/* Source link */}
              {req.source && (
                <a
                  href={req.source}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    marginTop: 12,
                    fontSize: 13,
                    color: "#2563eb",
                    fontWeight: 600,
                    textDecoration: "none",
                  }}
                >
                  <FaExternalLinkAlt style={{ fontSize: 11 }} /> View Property Page
                </a>
              )}

              {/* Action buttons */}
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 12, flexWrap: "wrap" }}>
                {latest.email && (
                  <a
                    href={`/user-activity?email=${encodeURIComponent(latest.email)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 5,
                      fontSize: 12,
                      fontWeight: 500,
                      color: "#7c3aed",
                      textDecoration: "none",
                      whiteSpace: "nowrap",
                    }}
                    onMouseEnter={e => { e.currentTarget.style.textDecoration = "underline"; }}
                    onMouseLeave={e => { e.currentTarget.style.textDecoration = "none"; }}
                  >
                    <FaHistory style={{ fontSize: 11 }} />
                    View Activity
                  </a>
                )}
                <button
                  onClick={() => {
                    const params = new URLSearchParams({ create: "true" });
                    if (req.address_text) params.set("address", req.address_text);
                    if (req.bedrooms) params.set("bedrooms", String(req.bedrooms));
                    if (req.bathrooms) params.set("bathrooms", String(req.bathrooms));
                    if (req.carspaces) params.set("parking", String(req.carspaces));
                    if (req.land_area) params.set("land_size", String(req.land_area));
                    if (req.property_type) params.set("property_type", req.property_type);
                    if (latest.email) params.set("contact_email", latest.email);
                    if (propertyId) params.set("property_id", propertyId);
                    navigate(`/leads-property-appraisals?${params.toString()}`);
                  }}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 5,
                    fontSize: 12,
                    fontWeight: 600,
                    color: "#2563eb",
                    background: "#eff6ff",
                    border: "1px solid #bfdbfe",
                    borderRadius: 6,
                    padding: "4px 12px",
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = "#dbeafe"; }}
                  onMouseLeave={e => { e.currentTarget.style.background = "#eff6ff"; }}
                >
                  <FaClipboardCheck style={{ fontSize: 11 }} />
                  Create Appraisal
                </button>
              </div>
            </div>

            {/* Legacy alert banner */}
            {isLegacy && (
              <div style={{
                display: "flex",
                alignItems: "flex-start",
                gap: 10,
                background: "#fff7ed",
                border: "1px solid #fed7aa",
                borderRadius: 12,
                padding: isMobile ? 14 : 18,
                marginBottom: 16,
                fontSize: 13,
                color: "#9a3412",
                lineHeight: 1.6,
              }}>
                <FaInfoCircle style={{ fontSize: 16, flexShrink: 0, marginTop: 2 }} />
                <div>
                  <strong>Limited data available</strong>
                  <p style={{ margin: "4px 0 0 0", color: "#b45309" }}>
                    This property analysis occurred before full tracking was introduced. Valuation estimates, rental data, and AI summaries are not available for this entry. You can still add review notes below.
                  </p>
                </div>
              </div>
            )}

            {/* Agent Notes card */}
            <div style={{
              background: "#fff",
              borderRadius: 12,
              padding: isMobile ? 16 : 24,
              boxShadow: "0 2px 8px #0001",
              border: "1px solid #e5e7eb",
              marginBottom: 16,
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                <FaStickyNote style={{ fontSize: 14, color: "#f59e42" }} />
                <h3 style={{ fontSize: 16, fontWeight: 700, color: "#1f2937", margin: 0 }}>Review Note</h3>
              </div>
              <p style={{ fontSize: 13, color: "#6b7280", margin: "0 0 10px 0", lineHeight: 1.5 }}>
                Add a note about this property. The homeowner will be notified that their property was reviewed by an agent, and your note will be used by the AI to improve future estimates and monthly property updates.
              </p>

              {/* AI Draft + Quick-add pills */}
              <div style={{ background: "#faf5ff", border: "1px solid #e9d5ff", borderRadius: 8, padding: 12, marginBottom: 10 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
                  <FaRobot style={{ fontSize: 12, color: "#8b5cf6" }} />
                  <span style={{ fontSize: 12, fontWeight: 700, color: "#7c3aed" }}>Draft with AI</span>
                  <span style={{ fontSize: 11, color: "#9ca3af", marginLeft: "auto" }}>Describe what you want to say, or use quick-adds below</span>
                </div>
                <div style={{ display: "flex", flexDirection: isMobile ? "column" : "row", gap: 8, alignItems: isMobile ? "stretch" : "flex-end" }}>
                  <textarea
                    ref={aiPromptRef}
                    value={aiPrompt}
                    onChange={e => {
                      setAiPrompt(e.target.value);
                      e.target.style.height = "0px";
                      e.target.style.height = Math.max(isMobile ? 48 : 36, e.target.scrollHeight) + "px";
                    }}
                    onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleAiGenerate(); } }}
                    placeholder="e.g. estimate looks low due to recent renos, great street"
                    rows={isMobile ? 2 : 1}
                    style={{
                      flex: 1, padding: "8px 10px", borderRadius: 6, border: "1px solid #d8b4fe",
                      fontSize: 13, fontFamily: "inherit", outline: "none", resize: "none",
                      overflow: "hidden", minHeight: isMobile ? 48 : 36, lineHeight: 1.5,
                    }}
                  />
                  <button
                    onClick={handleAiGenerate}
                    disabled={aiGenerating || !aiPrompt.trim()}
                    style={{
                      background: aiGenerating || !aiPrompt.trim() ? "#c4b5fd" : "#7c3aed",
                      color: "#fff", border: "none", borderRadius: 6, padding: "8px 16px",
                      fontWeight: 600, fontSize: 12, cursor: aiGenerating || !aiPrompt.trim() ? "not-allowed" : "pointer",
                      display: "flex", alignItems: "center", justifyContent: "center", gap: 5, whiteSpace: "nowrap", flexShrink: 0,
                    }}
                  >
                    {aiGenerating ? <Spinner animation="border" size="sm" style={{ width: 12, height: 12 }} /> : <FaRobot style={{ fontSize: 11 }} />}
                    {aiGenerating ? "Generating…" : "Generate"}
                  </button>
                </div>

                {/* Quick-add pills — append to AI prompt */}
                <div style={{ marginTop: 8 }}>
                  <div style={{ fontSize: 10, fontWeight: 600, color: "#b4a0d6", marginBottom: 5, textTransform: "uppercase", letterSpacing: 0.5 }}>
                    Quick add
                  </div>
                  {(() => {
                    const allPills = [
                      { short: "Too low — renos", label: "Estimate too low — doesn't account for recent renovations", icon: "⬆️", bg: "#fef2f2", color: "#dc2626", border: "#fca5a5" },
                      { short: "Too high — needs work", label: "Estimate too high — property needs significant work", icon: "⬇️", bg: "#eff6ff", color: "#2563eb", border: "#bfdbfe" },
                      { short: "Looks accurate", label: "Estimate looks accurate based on local market knowledge", icon: "✅", bg: "#f0fdf4", color: "#15803d", border: "#86efac" },
                      { short: "Too low — views", label: "Estimate too low — unique views/position not factored in", icon: "⬆️", bg: "#fef2f2", color: "#dc2626", border: "#fca5a5" },
                      { short: "Too high — comps", label: "Estimate too high — comparable sales suggest lower range", icon: "⬇️", bg: "#eff6ff", color: "#2563eb", border: "#bfdbfe" },
                      { short: "Renovated", label: "Recently renovated — kitchen/bathroom updated, adds value", icon: "🔨", bg: "#fff7ed", color: "#c2410c", border: "#fdba74" },
                      { short: "Pool / outdoor", label: "Pool/outdoor entertaining area not reflected in estimate", icon: "🏊", bg: "#ecfdf5", color: "#059669", border: "#6ee7b7" },
                      { short: "Corner block", label: "Corner block / larger-than-average land adds premium", icon: "📐", bg: "#faf5ff", color: "#7c3aed", border: "#c4b5fd" },
                      { short: "Granny flat", label: "Granny flat / dual income potential not captured", icon: "🏠", bg: "#fefce8", color: "#a16207", border: "#fde047" },
                      { short: "Street noise", label: "Street noise / busy road detracts — estimate may be optimistic", icon: "🔊", bg: "#f1f5f9", color: "#64748b", border: "#cbd5e1" },
                    ];
                    const visible = allPills.slice(0, 3);
                    const more = allPills.slice(3);
                    const appendPill = (pill) => {
                      setAiPrompt(prev => {
                        const trimmed = prev.trim();
                        return trimmed ? trimmed + ". " + pill.label : pill.label;
                      });
                    };
                    const pillButton = (pill, i) => (
                      <button
                        key={i}
                        onClick={() => appendPill(pill)}
                        style={{
                          display: "inline-flex", alignItems: "center", gap: 4,
                          borderRadius: 16, padding: "4px 12px", fontSize: 11, fontWeight: 600,
                          background: pill.bg, color: pill.color, border: `1px solid ${pill.border}`,
                          cursor: "pointer", transition: "transform 0.1s, box-shadow 0.1s", whiteSpace: "nowrap",
                        }}
                        onMouseEnter={e => { e.currentTarget.style.transform = "scale(1.03)"; e.currentTarget.style.boxShadow = "0 2px 8px #0001"; }}
                        onMouseLeave={e => { e.currentTarget.style.transform = "scale(1)"; e.currentTarget.style.boxShadow = "none"; }}
                      >
                        <span>{pill.icon}</span> {isMobile ? pill.short : pill.label}
                      </button>
                    );
                    return (
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
                        {visible.map((pill, i) => pillButton(pill, i))}
                        {/* More dropdown */}
                        <div ref={moreDropdownRef} style={{ position: "relative", display: "inline-block" }}>
                          <button
                            onClick={() => setMoreDropdownOpen(prev => !prev)}
                            style={{
                              display: "inline-flex", alignItems: "center", gap: 4,
                              borderRadius: 16, padding: "4px 12px", fontSize: 11, fontWeight: 600,
                              background: moreDropdownOpen ? "#e0e7ff" : "#f8fafc",
                              color: moreDropdownOpen ? "#4338ca" : "#64748b",
                              border: `1px solid ${moreDropdownOpen ? "#818cf8" : "#e2e8f0"}`,
                              cursor: "pointer", transition: "all 0.15s",
                            }}
                          >
                            More… <FaChevronDown style={{ fontSize: 8, transform: moreDropdownOpen ? "rotate(180deg)" : "none", transition: "transform 0.15s" }} />
                          </button>
                          {moreDropdownOpen && (
                            <div style={{
                              position: isMobile ? "fixed" : "absolute",
                              top: isMobile ? "auto" : "calc(100% + 6px)",
                              bottom: isMobile ? 0 : "auto",
                              left: isMobile ? 0 : 0,
                              right: isMobile ? 0 : "auto",
                              zIndex: 50,
                              background: "#fff", borderRadius: isMobile ? "14px 14px 0 0" : 10, border: "1px solid #e5e7eb",
                              boxShadow: isMobile ? "0 -4px 24px rgba(0,0,0,0.15)" : "0 8px 24px rgba(0,0,0,0.12)", padding: 8,
                              display: "flex", flexDirection: "column", gap: 4,
                              minWidth: isMobile ? "auto" : 280, maxWidth: isMobile ? "100%" : 420,
                              maxHeight: isMobile ? "60vh" : "none", overflowY: isMobile ? "auto" : "visible",
                            }}>
                              {more.map((pill, i) => (
                                <button
                                  key={i}
                                  onClick={() => appendPill(pill)}
                                  style={{
                                    display: "flex", alignItems: "center", gap: 6, width: "100%",
                                    borderRadius: 8, padding: "7px 10px", fontSize: 12, fontWeight: 500,
                                    background: "#fff", color: "#374151", border: "none",
                                    cursor: "pointer", textAlign: "left", transition: "background 0.12s",
                                  }}
                                  onMouseEnter={e => e.currentTarget.style.background = "#f3f4f6"}
                                  onMouseLeave={e => e.currentTarget.style.background = "#fff"}
                                >
                                  <span style={{
                                    display: "inline-flex", alignItems: "center", justifyContent: "center",
                                    width: 22, height: 22, borderRadius: "50%", fontSize: 11,
                                    background: pill.bg, border: `1px solid ${pill.border}`, flexShrink: 0,
                                  }}>{pill.icon}</span>
                                  <span style={{ whiteSpace: "normal", lineHeight: 1.35 }}>{pill.label}</span>
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>

              {/* Note output — editable */}
              <div style={{ fontSize: 11, fontWeight: 600, color: "#9ca3af", marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.5 }}>
                Note to send
              </div>
              <textarea
                ref={noteTextareaRef}
                value={note}
                onChange={e => { setNote(e.target.value); setNoteSaved(false); }}
                placeholder="Type directly, or use AI Draft above to generate a note…"
                rows={3}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  borderRadius: 8,
                  border: "1px solid #e5e7eb",
                  fontSize: 14,
                  boxSizing: "border-box",
                  resize: "vertical",
                  fontFamily: "inherit",
                  lineHeight: 1.5,
                  outline: "none",
                  overflow: "hidden",
                  minHeight: 72,
                }}
              />

              <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 10 }}>
                <button
                  onClick={handleSaveClick}
                  disabled={savingNote || !note.trim()}
                  style={{
                    background: savingNote || !note.trim() ? "#94a3b8" : "#2563eb",
                    color: "#fff",
                    border: "none",
                    borderRadius: 6,
                    padding: "7px 18px",
                    fontWeight: 600,
                    cursor: savingNote || !note.trim() ? "not-allowed" : "pointer",
                    fontSize: 13,
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                  }}
                >
                  {savingNote ? (
                    <Spinner animation="border" size="sm" style={{ width: 14, height: 14 }} />
                  ) : (
                    <FaStickyNote style={{ fontSize: 11 }} />
                  )}
                  {savingNote ? "Saving…" : "Save Note"}
                </button>
                {noteSaved && (
                  <span style={{ fontSize: 13, color: "#15803d", fontWeight: 600, display: "flex", alignItems: "center", gap: 4 }}>
                    <FaCheck style={{ fontSize: 11 }} /> Saved
                  </span>
                )}
              </div>

              {/* CMA documents — uploaded PDFs that the AI uses as context for
                  future valuations and monthly property updates. */}
              <div style={{ marginTop: 20, borderTop: "1px solid #e5e7eb", paddingTop: 16 }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 8, flexWrap: "wrap" }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "#1f2937", display: "flex", alignItems: "center", gap: 6 }}>
                      <FaPaperclip style={{ fontSize: 12, color: "#6b7280" }} /> CMA Documents
                      {cmas.length > 0 && (
                        <span style={{ fontSize: 11, color: "#6b7280", fontWeight: 500 }}>({cmas.length})</span>
                      )}
                    </div>
                    <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2, lineHeight: 1.4 }}>
                      Upload your CMA PDF for this property. The AI will use it as context for future valuations and monthly property updates.
                    </div>
                  </div>
                  <div>
                    <input
                      ref={cmaFileInputRef}
                      type="file"
                      accept="application/pdf,.pdf"
                      style={{ display: "none" }}
                      onChange={e => {
                        const f = e.target.files && e.target.files[0];
                        if (f) handleUploadCma(f);
                      }}
                    />
                    <button
                      onClick={() => cmaFileInputRef.current && cmaFileInputRef.current.click()}
                      disabled={uploadingCma}
                      style={{
                        background: uploadingCma ? "#94a3b8" : "#fff",
                        color: uploadingCma ? "#fff" : "#2563eb",
                        border: `1px solid ${uploadingCma ? "#94a3b8" : "#bfdbfe"}`,
                        borderRadius: 8,
                        padding: "6px 12px",
                        fontWeight: 600,
                        fontSize: 12,
                        cursor: uploadingCma ? "not-allowed" : "pointer",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 6,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {uploadingCma ? (
                        <>
                          <Spinner animation="border" size="sm" style={{ width: 12, height: 12 }} /> Uploading…
                        </>
                      ) : (
                        <>
                          <FaPaperclip style={{ fontSize: 11 }} /> Upload CMA PDF
                        </>
                      )}
                    </button>
                  </div>
                </div>
                {cmaUploadError && (
                  <div style={{ fontSize: 12, color: "#b91c1c", background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 6, padding: "6px 10px", marginBottom: 8 }}>
                    {cmaUploadError}
                  </div>
                )}
                {cmasLoading ? (
                  <div style={{ display: "flex", justifyContent: "center", padding: "8px 0" }}>
                    <Spinner animation="border" size="sm" />
                  </div>
                ) : cmas.length === 0 ? (
                  <div style={{ fontSize: 12, color: "#9ca3af", fontStyle: "italic", padding: "8px 0" }}>
                    No CMA documents uploaded yet.
                  </div>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    {cmas.map(c => (
                      <div
                        key={c.id}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          padding: "8px 10px",
                          background: "#f9fafb",
                          border: "1px solid #e5e7eb",
                          borderRadius: 8,
                          fontSize: 13,
                          color: "#1f2937",
                        }}
                      >
                        <FaPaperclip style={{ fontSize: 11, color: "#6b7280", flexShrink: 0 }} />
                        <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={c.original_filename}>
                          {c.original_filename || "CMA.pdf"}
                        </span>
                        {c.file_size_bytes != null && (
                          <span style={{ fontSize: 11, color: "#9ca3af", flexShrink: 0 }}>
                            {(c.file_size_bytes / 1024).toFixed(0)} KB
                          </span>
                        )}
                        <span style={{ fontSize: 11, color: "#9ca3af", flexShrink: 0 }}>
                          {c.created_at ? new Date(c.created_at * 1000).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" }) : ""}
                        </span>
                        {c.is_mine !== false && (
                          <button
                            onClick={() => handleDeleteCma(c.id)}
                            disabled={deletingCmaId === c.id}
                            title="Delete CMA"
                            style={{
                              background: "none",
                              border: "none",
                              cursor: deletingCmaId === c.id ? "not-allowed" : "pointer",
                              color: "#d1d5db",
                              padding: 4,
                              borderRadius: 4,
                              display: "flex",
                              alignItems: "center",
                              flexShrink: 0,
                            }}
                            onMouseEnter={e => { if (deletingCmaId !== c.id) e.currentTarget.style.color = "#ef4444"; }}
                            onMouseLeave={e => { e.currentTarget.style.color = "#d1d5db"; }}
                          >
                            {deletingCmaId === c.id
                              ? <Spinner animation="border" size="sm" style={{ width: 12, height: 12 }} />
                              : <FaTrash style={{ fontSize: 11 }} />
                            }
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Save Note Confirmation Modal */}
              {showNoteModal && (
                <div
                  onClick={() => setShowNoteModal(false)}
                  style={{
                    position: "fixed",
                    inset: 0,
                    background: "rgba(0,0,0,0.4)",
                    zIndex: 1000,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: 16,
                    overflow: "hidden",
                  }}
                >
                  <div
                    onClick={e => e.stopPropagation()}
                    style={{
                      background: "#fff",
                      borderRadius: 14,
                      maxWidth: 460,
                      width: "100%",
                      maxHeight: "85vh",
                      boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
                      position: "relative",
                      display: "flex",
                      flexDirection: "column",
                      overflow: "hidden",
                    }}
                  >
                    {/* Modal header — fixed */}
                    <div style={{ padding: "20px 24px 0 24px", flexShrink: 0 }}>
                      <button
                        onClick={() => setShowNoteModal(false)}
                        style={{
                          position: "absolute",
                          top: 12,
                          right: 12,
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          color: "#9ca3af",
                          fontSize: 16,
                          padding: 4,
                        }}
                      >
                        <FaTimes />
                      </button>
                      <h3 style={{ fontSize: 17, fontWeight: 700, color: "#1f2937", margin: "0 0 6px 0" }}>Save Note</h3>
                      <p style={{ fontSize: 13, color: "#6b7280", margin: "0 0 0 0", lineHeight: 1.5 }}>
                        Choose how this note is shared and used.
                      </p>
                    </div>

                    {/* Modal body — scrollable */}
                    <div style={{ overflowY: "auto", padding: "16px 24px", flex: 1, minHeight: 0 }}>
                      {/* Note preview */}
                      <div style={{
                        background: "#f9fafb",
                        borderRadius: 8,
                        padding: "10px 12px",
                        fontSize: 13,
                        color: "#374151",
                        lineHeight: 1.5,
                        marginBottom: 18,
                        border: "1px solid #f3f4f6",
                        maxHeight: 120,
                        overflowY: "auto",
                        whiteSpace: "pre-wrap",
                      }}>
                        {note.trim()}
                      </div>

                      {/* Checkboxes */}
                      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                        {channels.has_email && (
                          <label style={{ display: "flex", gap: 10, cursor: "pointer", alignItems: "flex-start" }}>
                            <input
                              type="checkbox"
                              checked={noteOptions.notify_email}
                              onChange={e => setNoteOptions(prev => ({ ...prev, notify_email: e.target.checked }))}
                              style={{ marginTop: 3, accentColor: "#2563eb", width: 16, height: 16, flexShrink: 0 }}
                            />
                            <div>
                              <div style={{ fontSize: 13, fontWeight: 600, color: "#1f2937", display: "flex", alignItems: "center", gap: 5 }}>
                                <FaEnvelope style={{ fontSize: 11, color: "#f59e42" }} /> Notify homeowner by email
                              </div>
                              <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2, lineHeight: 1.4 }}>
                                The homeowner will receive an email that an agent has reviewed their property and left a note.
                              </div>
                            </div>
                          </label>
                        )}
                        {channels.has_sms && (() => {
                          const noteText = note.trim();
                          const parts = smsPartCount(noteText);
                          const cost = smsCost(noteText);
                          const limits = smsPartLimits(noteText);
                          const charLimit = parts <= 1 ? limits.single : limits.multi;
                          const have = Number(balance?.balance_amount ?? 0);
                          const insufficient = noteOptions.notify_sms && !balanceLoading && cost > have + 1e-9;
                          return (
                            <label style={{ display: "flex", gap: 10, cursor: "pointer", alignItems: "flex-start" }}>
                              <input
                                type="checkbox"
                                checked={noteOptions.notify_sms}
                                onChange={e => setNoteOptions(prev => ({ ...prev, notify_sms: e.target.checked }))}
                                style={{ marginTop: 3, accentColor: "#2563eb", width: 16, height: 16, flexShrink: 0 }}
                              />
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: 13, fontWeight: 600, color: "#1f2937", display: "flex", alignItems: "center", gap: 5 }}>
                                  <FaSms style={{ fontSize: 12, color: "#22c55e" }} /> Notify homeowner by SMS
                                </div>
                                <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2, lineHeight: 1.4 }}>
                                  The homeowner will receive an SMS with the note and a link to their property page.
                                </div>
                                <div style={{
                                  fontSize: 11.5,
                                  color: "#4b5563",
                                  marginTop: 6,
                                  lineHeight: 1.45,
                                  background: "#f8fafc",
                                  border: "1px solid #e5e7eb",
                                  borderRadius: 6,
                                  padding: "6px 8px",
                                }}>
                                  SMS is charged at <strong>A${SMS_PRICE_PER_PART.toFixed(2)} per message part</strong>
                                  {" "}(1 part = up to {limits.single} characters; longer messages are split into {limits.multi}-character parts).
                                  {" "}Your note is <strong>{noteText.length} character{noteText.length === 1 ? "" : "s"}</strong> ={">"} <strong>{parts} part{parts === 1 ? "" : "s"}</strong> ={">"} <strong>A${cost.toFixed(2)}</strong>.
                                  {" "}<span style={{ color: "#9ca3af" }}>Up to {charLimit - noteText.length >= 0 ? (charLimit - noteText.length) : 0} more character{(charLimit - noteText.length) === 1 ? "" : "s"} before the next part.</span>
                                </div>
                                {noteOptions.notify_sms && !balanceLoading && (
                                  <div style={{
                                    fontSize: 11.5,
                                    marginTop: 6,
                                    color: insufficient ? "#92400e" : "#15803d",
                                    background: insufficient ? "#fef3c7" : "#f0fdf4",
                                    border: `1px solid ${insufficient ? "#fde68a" : "#bbf7d0"}`,
                                    borderRadius: 6,
                                    padding: "6px 8px",
                                    lineHeight: 1.45,
                                  }}>
                                    {insufficient ? (
                                      <>Your extra-usage balance (<strong>A${have.toFixed(2)}</strong>) isn't enough to cover this SMS (<strong>A${cost.toFixed(2)}</strong>). You'll be prompted to top up when you confirm.</>
                                    ) : (
                                      <>Extra-usage balance: <strong>A${have.toFixed(2)}</strong> — enough for this SMS.</>
                                    )}
                                  </div>
                                )}
                              </div>
                            </label>
                          );
                        })()}
                        {channels.loaded && !channels.has_email && !channels.has_sms && (
                          <div style={{
                            fontSize: 12, color: "#6b7280", lineHeight: 1.5,
                            background: "#fef3c7", border: "1px solid #fde68a",
                            borderRadius: 8, padding: "8px 10px",
                          }}>
                            No email or mobile on file for this contact — the note will be saved but the homeowner won't be notified.
                          </div>
                        )}
                        <label style={{ display: "flex", gap: 10, cursor: "pointer", alignItems: "flex-start" }}>
                          <input
                            type="checkbox"
                            checked={noteOptions.visible_to_user}
                            onChange={e => setNoteOptions(prev => ({ ...prev, visible_to_user: e.target.checked }))}
                            style={{ marginTop: 3, accentColor: "#2563eb", width: 16, height: 16, flexShrink: 0 }}
                          />
                          <div>
                            <div style={{ fontSize: 13, fontWeight: 600, color: "#1f2937", display: "flex", alignItems: "center", gap: 5 }}>
                              <FaEye style={{ fontSize: 11, color: "#2563eb" }} /> Visible to homeowner
                            </div>
                            <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2, lineHeight: 1.4 }}>
                              The homeowner will be able to see this note on their property profile page.
                            </div>
                          </div>
                        </label>
                        <label style={{ display: "flex", gap: 10, cursor: "pointer", alignItems: "flex-start" }}>
                          <input
                            type="checkbox"
                            checked={noteOptions.visible_to_ai}
                            onChange={e => setNoteOptions(prev => ({ ...prev, visible_to_ai: e.target.checked }))}
                            style={{ marginTop: 3, accentColor: "#2563eb", width: 16, height: 16, flexShrink: 0 }}
                          />
                          <div>
                            <div style={{ fontSize: 13, fontWeight: 600, color: "#1f2937", display: "flex", alignItems: "center", gap: 5 }}>
                              <FaRobot style={{ fontSize: 11, color: "#7c3aed" }} /> Used by AI
                            </div>
                            <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2, lineHeight: 1.4 }}>
                              The AI will use this note to improve future property valuations, market summaries, and monthly property update reports.
                            </div>
                          </div>
                        </label>
                      </div>
                    </div>

                    {/* Modal footer — fixed */}
                    <div style={{ padding: "12px 24px 20px 24px", flexShrink: 0, display: "flex", gap: 10, justifyContent: "flex-end", borderTop: "1px solid #f3f4f6" }}>
                      <button
                        onClick={() => setShowNoteModal(false)}
                        style={{
                          background: "#f3f4f6",
                          color: "#374151",
                          border: "1px solid #e5e7eb",
                          borderRadius: 8,
                          padding: "8px 18px",
                          fontWeight: 600,
                          fontSize: 13,
                          cursor: "pointer",
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleConfirmSaveNote}
                        style={{
                          background: "#2563eb",
                          color: "#fff",
                          border: "none",
                          borderRadius: 8,
                          padding: "8px 18px",
                          fontWeight: 600,
                          fontSize: 13,
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                        }}
                      >
                        <FaCheck style={{ fontSize: 10 }} /> Confirm & Save
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Buy extra usage modal — popped on top of the Save Note modal
                  when the agent doesn't have enough credit to cover the SMS. */}
              {showBuyCreditModal && (
                <BuyExtraUsageModal
                  onClose={() => {
                    setShowBuyCreditModal(false);
                    fetchBalance();
                    // Restore the Save Note modal so the agent can retry the
                    // confirm step once their balance has been topped up.
                    setShowNoteModal(true);
                  }}
                  defaultAmount={buyCreditSuggested}
                  returnUrl={`${window.location.origin}${window.location.pathname}?note=true`}
                  reason={`You don't have enough extra usage to send this SMS (cost A$${smsCost(note.trim()).toFixed(2)}, balance A$${Number(balance?.balance_amount ?? 0).toFixed(2)}). Top up to continue.`}
                />
              )}

              {/* Notes timeline */}
              {notesLoading ? (
                <div style={{ display: "flex", justifyContent: "center", padding: "20px 0" }}>
                  <Spinner animation="border" size="sm" />
                </div>
              ) : notes.length > 0 && (
                <div style={{ marginTop: 20, borderTop: "1px solid #e5e7eb", paddingTop: 16 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "#6b7280", marginBottom: 12 }}>
                    Note History ({notes.length})
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
                    {notes.map((n, i) => (
                      <div
                        key={n.id || i}
                        style={{
                          display: "flex",
                          gap: 12,
                          position: "relative",
                        }}
                      >
                        {/* Timeline line + dot */}
                        <div style={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          width: 16,
                          flexShrink: 0,
                        }}>
                          <div style={{
                            width: 10,
                            height: 10,
                            borderRadius: "50%",
                            background: i === 0 ? "#2563eb" : "#d1d5db",
                            border: i === 0 ? "2px solid #bfdbfe" : "2px solid #e5e7eb",
                            flexShrink: 0,
                            marginTop: 4,
                          }} />
                          {i < notes.length - 1 && (
                            <div style={{
                              width: 2,
                              flex: 1,
                              background: "#e5e7eb",
                              minHeight: 16,
                            }} />
                          )}
                        </div>
                        {/* Note content */}
                        <div style={{
                          flex: 1,
                          paddingBottom: i < notes.length - 1 ? 16 : 0,
                        }}>
                          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
                            <div style={{ fontSize: 12, color: "#9ca3af" }}>
                              {formatDate(n.created_at)}
                              {i === 0 && (
                                <span style={{
                                  marginLeft: 8,
                                  fontSize: 11,
                                  fontWeight: 600,
                                  color: "#2563eb",
                                  background: "#eff6ff",
                                  borderRadius: 10,
                                  padding: "1px 8px",
                                }}>Latest</span>
                              )}
                              {n.agent_name && (
                                <span style={{
                                  marginLeft: 8,
                                  fontSize: 11,
                                  fontWeight: 600,
                                  color: "#6d28d9",
                                  background: "#ede9fe",
                                  borderRadius: 10,
                                  padding: "1px 8px",
                                }}>{n.agent_name}</span>
                              )}
                            </div>
                            <button
                              onClick={() => handleDeleteNote(n.id)}
                              disabled={deletingNoteId === n.id || n.is_mine === false}
                              title="Delete note"
                              style={{
                                background: "none",
                                border: "none",
                                cursor: deletingNoteId === n.id ? "not-allowed" : "pointer",
                                color: deletingNoteId === n.id ? "#d1d5db" : "#d1d5db",
                                padding: 4,
                                borderRadius: 4,
                                display: "flex",
                                alignItems: "center",
                                transition: "color 0.15s",
                              }}
                              onMouseEnter={e => { if (deletingNoteId !== n.id) e.currentTarget.style.color = "#ef4444"; }}
                              onMouseLeave={e => { e.currentTarget.style.color = "#d1d5db"; }}
                            >
                              {deletingNoteId === n.id
                                ? <Spinner animation="border" size="sm" style={{ width: 12, height: 12 }} />
                                : <FaTrash style={{ fontSize: 11 }} />
                              }
                            </button>
                          </div>
                          <div style={{
                            fontSize: 14,
                            color: "#374151",
                            lineHeight: 1.6,
                            whiteSpace: "pre-wrap",
                          }}>
                            {n.note}
                          </div>
                          {/* Note option badges */}
                          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 6 }}>
                            {n.notify_user && (
                              <span style={{
                                display: "inline-flex", alignItems: "center", gap: 4,
                                fontSize: 10, fontWeight: 600, borderRadius: 10, padding: "1px 8px",
                                background: "#fff7ed", color: "#c2410c", border: "1px solid #fed7aa",
                              }}>
                                <FaBell style={{ fontSize: 9 }} /> Notified
                              </span>
                            )}
                            {n.visible_to_user && (
                              <span style={{
                                display: "inline-flex", alignItems: "center", gap: 4,
                                fontSize: 10, fontWeight: 600, borderRadius: 10, padding: "1px 8px",
                                background: "#eff6ff", color: "#2563eb", border: "1px solid #bfdbfe",
                              }}>
                                <FaEye style={{ fontSize: 9 }} /> Visible
                              </span>
                            )}
                            {n.visible_to_ai && (
                              <span style={{
                                display: "inline-flex", alignItems: "center", gap: 4,
                                fontSize: 10, fontWeight: 600, borderRadius: 10, padding: "1px 8px",
                                background: "#f5f3ff", color: "#7c3aed", border: "1px solid #c4b5fd",
                              }}>
                                <FaRobot style={{ fontSize: 9 }} /> AI
                              </span>
                            )}
                            {n.notify_user === false && n.visible_to_user === false && n.visible_to_ai === false && (
                              <span style={{ fontSize: 10, color: "#9ca3af", fontStyle: "italic" }}>Private note</span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Estimates & AI Analysis card — unified with activity history selector */}
            <div style={{
              background: "#fff",
              borderRadius: 12,
              boxShadow: "0 2px 8px #0001",
              border: "1px solid #e5e7eb",
              marginBottom: 16,
              overflow: "hidden",
            }}>
              {/* Activity history selector */}
              {entries.length > 1 && (
                <div style={{
                  padding: isMobile ? "12px 16px" : "12px 24px",
                  borderBottom: "1px solid #e5e7eb",
                  background: "#f9fafb",
                }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: "#6b7280", marginBottom: 8, textTransform: "uppercase", letterSpacing: 0.5 }}>Activity History</div>
                  <div ref={activityDropdownRef} style={{ position: "relative", display: "inline-block" }}>
                    {/* Trigger button */}
                    <button
                      onClick={() => setActivityDropdownOpen(prev => !prev)}
                      style={{
                        display: "inline-flex", alignItems: "center", gap: 8,
                        padding: "7px 14px", background: activityDropdownOpen ? "#eff6ff" : "#fff",
                        border: `1.5px solid ${activityDropdownOpen ? "#2563eb" : "#e5e7eb"}`,
                        borderRadius: 8, fontSize: 13, fontWeight: 600,
                        color: "#1d4ed8", cursor: "pointer", transition: "all 0.15s",
                        outline: "none", fontFamily: "inherit",
                      }}
                    >
                      <span style={{
                        display: "inline-block", width: 7, height: 7, borderRadius: "50%",
                        background: "#2563eb", flexShrink: 0,
                      }} />
                      <span style={{
                        fontSize: 10, fontWeight: 600, padding: "0 6px", borderRadius: 10,
                        background: (entries[selectedEntryIndex]?.metadata?.request?.report_type === "property_report") ? "#dbeafe" : "#ede9fe",
                        color: (entries[selectedEntryIndex]?.metadata?.request?.report_type === "property_report") ? "#2563eb" : "#7c3aed",
                      }}>
                        {(entries[selectedEntryIndex]?.metadata?.request?.report_type === "property_report") ? "Report" : "View"}
                      </span>
                      {formatDate(entries[selectedEntryIndex]?.created_at)}
                      {selectedEntryIndex === 0 && <span style={{ fontSize: 10, color: "#2563eb", fontWeight: 700 }}>Latest</span>}
                      <FaChevronDown style={{ fontSize: 9, color: "#2563eb", transform: activityDropdownOpen ? "rotate(180deg)" : "none", transition: "transform 0.15s" }} />
                    </button>

                    {/* Dropdown panel */}
                    {activityDropdownOpen && (
                      <div style={{
                        position: "absolute", top: "calc(100% + 6px)", left: 0, zIndex: 50,
                        background: "#fff", borderRadius: 10, border: "1px solid #e5e7eb",
                        boxShadow: "0 8px 24px rgba(0,0,0,0.12)", padding: 6,
                        display: "flex", flexDirection: "column", gap: 2,
                        minWidth: 260, maxWidth: 380,
                      }}>
                        {entries.map((entry, i) => {
                          const eReq = entry.metadata?.request || {};
                          const isSelected = i === selectedEntryIndex;
                          return (
                            <button
                              key={i}
                              onClick={() => { setSelectedEntryIndex(i); setActivityDropdownOpen(false); }}
                              style={{
                                display: "flex", alignItems: "center", gap: 8, width: "100%",
                                padding: "7px 10px", borderRadius: 8, border: "none",
                                background: isSelected ? "#eff6ff" : "#fff",
                                cursor: "pointer", transition: "background 0.12s",
                                fontFamily: "inherit", outline: "none", textAlign: "left",
                              }}
                              onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = "#f3f4f6"; }}
                              onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = "#fff"; }}
                            >
                              <span style={{
                                display: "inline-block", width: 7, height: 7, borderRadius: "50%",
                                background: isSelected ? "#2563eb" : "#d1d5db", flexShrink: 0,
                              }} />
                              <span style={{
                                fontSize: 10, fontWeight: 600, padding: "0 6px", borderRadius: 10,
                                background: eReq.report_type === "property_report" ? (isSelected ? "#dbeafe" : "#f1f5f9") : (isSelected ? "#ede9fe" : "#f1f5f9"),
                                color: eReq.report_type === "property_report" ? "#2563eb" : "#7c3aed",
                              }}>
                                {eReq.report_type === "property_report" ? "Report" : "View"}
                              </span>
                              <span style={{ fontSize: 12, fontWeight: isSelected ? 700 : 500, color: isSelected ? "#1d4ed8" : "#6b7280" }}>
                                {formatDate(entry.created_at)}
                              </span>
                              {i === 0 && <span style={{ fontSize: 10, color: "#2563eb", fontWeight: 700 }}>Latest</span>}
                              {isSelected && <FaCheck style={{ fontSize: 10, color: "#2563eb", marginLeft: "auto" }} />}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Estimates for selected entry */}
              {(selectedMeta.valuation != null || selectedMeta.rental_estimate != null) && (
                <div style={{ padding: isMobile ? 16 : 24, borderBottom: (selectedReq.property_notes?.length || selectedMeta.ai_summary) ? "1px solid #e5e7eb" : "none" }}>
                  <h3 style={{ fontSize: 16, fontWeight: 700, color: "#1f2937", margin: "0 0 16px 0" }}>Estimates</h3>
                  <div style={{ display: "flex", gap: isMobile ? 16 : 32, flexWrap: "wrap" }}>
                    {selectedMeta.valuation != null && (
                      <div style={{ flex: 1, minWidth: 200 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
                          <FaHome style={{ fontSize: 14, color: "#2563eb" }} />
                          <span style={{ fontSize: 13, fontWeight: 600, color: "#6b7280" }}>Sale Estimate</span>
                        </div>
                        <div style={{ fontSize: 26, fontWeight: 700, color: "#1f2937", marginBottom: 4 }}>
                          {formatCurrency(selectedMeta.valuation)}
                        </div>
                        {selectedMeta.valuation_lower != null && selectedMeta.valuation_upper != null && (
                          <div style={{ fontSize: 13, color: "#9ca3af" }}>
                            Range: {formatCurrency(selectedMeta.valuation_lower)} – {formatCurrency(selectedMeta.valuation_upper)}
                          </div>
                        )}
                        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginTop: 8, fontSize: 12 }}>
                          {selectedMeta.valuation_percent_change_last_month != null && (
                            <span style={{ color: selectedMeta.valuation_percent_change_last_month >= 0 ? "#15803d" : "#e11d48", fontWeight: 600 }}>
                              {selectedMeta.valuation_percent_change_last_month >= 0 ? "↑" : "↓"} {Math.abs(selectedMeta.valuation_percent_change_last_month)}% MoM
                            </span>
                          )}
                          {selectedMeta.valuation_percent_change_year_ago != null && (
                            <span style={{ color: selectedMeta.valuation_percent_change_year_ago >= 0 ? "#15803d" : "#e11d48", fontWeight: 600 }}>
                              {selectedMeta.valuation_percent_change_year_ago >= 0 ? "↑" : "↓"} {Math.abs(selectedMeta.valuation_percent_change_year_ago)}% YoY
                            </span>
                          )}
                          {selectedMeta.valuation_percent_change_last_transacted != null && (
                            <span style={{ color: selectedMeta.valuation_percent_change_last_transacted >= 0 ? "#15803d" : "#e11d48", fontWeight: 600 }}>
                              {selectedMeta.valuation_percent_change_last_transacted >= 0 ? "↑" : "↓"} {Math.abs(selectedMeta.valuation_percent_change_last_transacted)}% since last sold
                            </span>
                          )}
                        </div>
                        {selectedReq.last_sold_price != null && (
                          <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 6 }}>
                            Last sold: {formatCurrency(selectedReq.last_sold_price)} ({selectedReq.last_sold_date ? formatDate(selectedReq.last_sold_date) : "—"})
                          </div>
                        )}
                        {selectedMeta.days_to_sell != null && (
                          <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 4 }}>
                            Est. days to sell: {selectedMeta.days_to_sell}
                            {selectedMeta.days_to_sell_lower != null && selectedMeta.days_to_sell_upper != null && (
                              <span> ({selectedMeta.days_to_sell_lower}–{selectedMeta.days_to_sell_upper})</span>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                    {selectedMeta.rental_estimate != null && (
                      <div style={{ flex: 1, minWidth: 200 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
                          <FaChartLine style={{ fontSize: 14, color: "#7c3aed" }} />
                          <span style={{ fontSize: 13, fontWeight: 600, color: "#6b7280" }}>Rental Estimate</span>
                        </div>
                        <div style={{ fontSize: 26, fontWeight: 700, color: "#1f2937", marginBottom: 4 }}>
                          {formatCurrency(selectedMeta.rental_estimate)}<span style={{ fontSize: 14, fontWeight: 500, color: "#6b7280" }}>/wk</span>
                        </div>
                        {selectedMeta.rental_estimate_lower != null && selectedMeta.rental_estimate_upper != null && (
                          <div style={{ fontSize: 13, color: "#9ca3af" }}>
                            Range: {formatCurrency(selectedMeta.rental_estimate_lower)} – {formatCurrency(selectedMeta.rental_estimate_upper)}/wk
                          </div>
                        )}
                        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginTop: 8, fontSize: 12 }}>
                          {selectedMeta.rental_estimate_percent_change_last_month != null && (
                            <span style={{ color: selectedMeta.rental_estimate_percent_change_last_month >= 0 ? "#15803d" : "#e11d48", fontWeight: 600 }}>
                              {selectedMeta.rental_estimate_percent_change_last_month >= 0 ? "↑" : "↓"} {Math.abs(selectedMeta.rental_estimate_percent_change_last_month)}% MoM
                            </span>
                          )}
                          {selectedMeta.rental_estimate_percent_change_year_ago != null && (
                            <span style={{ color: selectedMeta.rental_estimate_percent_change_year_ago >= 0 ? "#15803d" : "#e11d48", fontWeight: 600 }}>
                              {selectedMeta.rental_estimate_percent_change_year_ago >= 0 ? "↑" : "↓"} {Math.abs(selectedMeta.rental_estimate_percent_change_year_ago)}% YoY
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Comparable Analyses for selected entry */}
              {(() => {
                const _comps = (selectedReq.comparable_analyses && Array.isArray(selectedReq.comparable_analyses) && selectedReq.comparable_analyses.length > 0)
                  ? selectedReq.comparable_analyses
                  : (selectedReq.comparables && Array.isArray(selectedReq.comparables) && selectedReq.comparables.length > 0)
                    ? selectedReq.comparables
                    : null;
                if (!_comps) return null;
                const isRental = (c) => c.listing_type === 'Rent' || c.listing_type === 'Leased';
                const saleComps = _comps.filter(c => !isRental(c));
                const rentalComps = _comps.filter(c => isRental(c));
                const hasBoth = saleComps.length > 0 && rentalComps.length > 0;
                // Pick the visible group. When only one type exists, always show it.
                const activeTab = !hasBoth ? (rentalComps.length > 0 ? "rental" : "sale") : compTab;
                const activeComps = activeTab === "rental" ? rentalComps : saleComps;
                const tabBtn = (key, label, count) => {
                  const active = activeTab === key;
                  return (
                    <button
                      key={key}
                      onClick={() => setCompTab(key)}
                      style={{
                        flex: 1, cursor: "pointer", border: "none", borderRadius: 8,
                        padding: "7px 14px", fontSize: 13, fontWeight: 600,
                        background: active ? "#fff" : "transparent",
                        color: active ? "#1f2937" : "#6b7280",
                        boxShadow: active ? "0 1px 3px rgba(0,0,0,0.12)" : "none",
                        transition: "all 0.15s ease",
                      }}
                    >
                      {label} <span style={{ color: active ? "#2563eb" : "#9ca3af", fontWeight: 700 }}>{count}</span>
                    </button>
                  );
                };
                return (
                <div style={{ padding: isMobile ? 16 : 24, borderBottom: (selectedReq.property_notes?.length || selectedMeta.ai_summary || selectedMeta.history?.length) ? "1px solid #e5e7eb" : "none" }}>
                  <h3 style={{ fontSize: 16, fontWeight: 700, color: "#1f2937", margin: "0 0 16px 0" }}>Comparable Properties</h3>
                  {hasBoth && (
                    <div style={{ display: "flex", gap: 4, background: "#f3f4f6", borderRadius: 10, padding: 4, marginBottom: 16, maxWidth: isMobile ? "100%" : 320 }}>
                      {tabBtn("sale", "Sales", saleComps.length)}
                      {tabBtn("rental", "Rentals", rentalComps.length)}
                    </div>
                  )}
                  <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 14 }}>
                    {activeComps.map((comp, idx) => {
                      const scoreLabels = ['', 'Low', 'Fair', 'Good', 'Strong', 'Excellent'];
                      const scoreColors = ['', '#ef4444', '#f59e0b', '#22c55e', '#10b981', '#059669'];
                      const scoreBgColors = ['', '#fef2f2', '#fffbeb', '#f0fdf4', '#ecfdf5', '#ecfdf5'];
                      const s = comp.score != null ? Math.max(1, Math.min(5, Math.round(comp.score))) : null;
                      const diff = comp.diff;
                      const loc = comp.location;
                      const advStyles = { better: { color: '#dc2626', label: 'Better location' }, worse: { color: '#16a34a', label: 'Your property better' }, similar: { color: '#6b7280', label: 'Similar location' } };
                      const parsedLandArea = (() => {
                        if (comp.land_area == null) return null;
                        if (typeof comp.land_area === 'number') return comp.land_area;
                        try { return JSON.parse(comp.land_area)?.size || null; } catch { return null; }
                      })();
                      const dateStr = (() => {
                        const raw = comp.filter_date || comp.sold_timestamp;
                        if (!raw) return null;
                        const d = new Date(raw);
                        if (isNaN(d)) return null;
                        return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
                      })();
                      const daysAgo = (() => {
                        const raw = comp.filter_date || comp.sold_timestamp;
                        if (!raw) return null;
                        const d = new Date(raw);
                        if (isNaN(d)) return null;
                        return Math.round((Date.now() - d.getTime()) / (1000 * 60 * 60 * 24));
                      })();
                      const dateLabel = comp.listing_type === 'Rent' || comp.listing_type === 'Leased' ? 'Leased' : 'Sold';

                      return (
                        <div key={idx} style={{
                          borderRadius: 16, overflow: "hidden", background: "#fff",
                          boxShadow: "0 2px 16px rgba(0,0,0,0.06)",
                          border: "1px solid #e5e7eb",
                        }}>
                          {/* Image + score badge */}
                          {comp.photo_url_main ? (
                            <div style={{ position: "relative" }}>
                              <img src={comp.photo_url_main} alt={comp.address} style={{ width: "100%", height: isMobile ? 180 : 200, objectFit: "cover", display: "block" }} onError={e => { e.target.style.display = "none"; }} />
                              {s != null && (
                                <div style={{ position: "absolute", top: 10, right: 10, display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 9px", borderRadius: 20, backgroundColor: scoreBgColors[s], border: `1px solid ${scoreColors[s]}20` }}>
                                  <div style={{ display: "flex", gap: 2 }}>{[1,2,3,4,5].map(i => <FaStar key={i} size={8} style={{ color: i <= s ? scoreColors[s] : "#e5e7eb" }} />)}</div>
                                  <span style={{ fontSize: 10, fontWeight: 600, color: scoreColors[s], letterSpacing: "0.02em" }}>{scoreLabels[s]} Match</span>
                                </div>
                              )}
                            </div>
                          ) : s != null ? (
                            <div style={{ padding: "10px 14px 0" }}>
                              <div style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 9px", borderRadius: 20, backgroundColor: scoreBgColors[s], border: `1px solid ${scoreColors[s]}20` }}>
                                <div style={{ display: "flex", gap: 2 }}>{[1,2,3,4,5].map(i => <FaStar key={i} size={8} style={{ color: i <= s ? scoreColors[s] : "#e5e7eb" }} />)}</div>
                                <span style={{ fontSize: 10, fontWeight: 600, color: scoreColors[s], letterSpacing: "0.02em" }}>{scoreLabels[s]} Match</span>
                              </div>
                            </div>
                          ) : null}

                          {/* Card body */}
                          <div style={{ padding: isMobile ? "14px 16px" : "18px 22px" }}>
                            <div style={{ fontWeight: 600, fontSize: 14, color: "#1a1a1a", marginBottom: 4, letterSpacing: "-0.01em" }}>
                              {comp.address || "Unknown Address"}
                            </div>
                            {(comp.display_price || comp.price) && (
                              <div style={{ fontWeight: 700, fontSize: 17, color: "#2563eb", marginBottom: 8 }}>
                                {typeof (comp.display_price || comp.price) === 'string' ? (comp.display_price || comp.price) : formatCurrency(comp.display_price || comp.price)}
                              </div>
                            )}

                            {/* Features row */}
                            <div style={{ display: "flex", gap: 12, flexWrap: "wrap", fontSize: 12, color: "#9ca3af", marginBottom: 10 }}>
                              {comp.bedrooms != null && <span><FaBed size={11} style={{ marginRight: 3 }} />{comp.bedrooms}</span>}
                              {comp.bathrooms != null && <span><FaBath size={11} style={{ marginRight: 3 }} />{comp.bathrooms}</span>}
                              {comp.carspaces != null && <span><FaCar size={11} style={{ marginRight: 3 }} />{comp.carspaces}</span>}
                              {parsedLandArea != null && parsedLandArea > 0 && <span><FaRulerCombined size={10} style={{ marginRight: 3 }} />{parsedLandArea}m²</span>}
                              {dateStr && <span><FaCalendarAlt size={10} style={{ marginRight: 3 }} />{dateLabel} {dateStr}{daysAgo != null ? ` (${daysAgo}d ago)` : ''}</span>}
                            </div>

                            {/* Property type chip (shown for raw comparables without analysis) */}
                            {comp.property_type && s == null && (
                              <div style={{ marginBottom: 10 }}>
                                <span style={{ display: 'inline-block', fontSize: 10, fontWeight: 500, color: '#6b7280', backgroundColor: '#f3f4f6', padding: '2px 8px', borderRadius: 12 }}>
                                  {comp.property_type}
                                </span>
                              </div>
                            )}

                            {/* Diff chips */}
                            {diff && typeof diff === "object" && Object.keys(diff).length > 0 && (
                              <div style={{ display: "flex", gap: 5, flexWrap: "wrap", marginBottom: comp.summary ? 10 : 0 }}>
                                {diff.bedrooms != null && (() => { const v = diff.bedrooms; const isZ = v === 0; const isP = v > 0; const c = isZ ? '#6b7280' : isP ? '#16a34a' : '#dc2626'; const bg = isZ ? '#f3f4f6' : isP ? '#f0fdf4' : '#fef2f2'; return <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 10, fontWeight: 500, color: c, backgroundColor: bg, padding: '2px 8px', borderRadius: 12 }}>{isZ ? <FaMinus size={6}/> : isP ? <FaArrowUp size={6}/> : <FaArrowDown size={6}/>} {isZ ? 'Same beds' : `${isP?'+':''}${v} beds`}</span>; })()}
                                {diff.bathrooms != null && (() => { const v = diff.bathrooms; const isZ = v === 0; const isP = v > 0; const c = isZ ? '#6b7280' : isP ? '#16a34a' : '#dc2626'; const bg = isZ ? '#f3f4f6' : isP ? '#f0fdf4' : '#fef2f2'; return <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 10, fontWeight: 500, color: c, backgroundColor: bg, padding: '2px 8px', borderRadius: 12 }}>{isZ ? <FaMinus size={6}/> : isP ? <FaArrowUp size={6}/> : <FaArrowDown size={6}/>} {isZ ? 'Same baths' : `${isP?'+':''}${v} baths`}</span>; })()}
                                {diff.parking != null && (() => { const v = diff.parking; const isZ = v === 0; const isP = v > 0; const c = isZ ? '#6b7280' : isP ? '#16a34a' : '#dc2626'; const bg = isZ ? '#f3f4f6' : isP ? '#f0fdf4' : '#fef2f2'; return <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 10, fontWeight: 500, color: c, backgroundColor: bg, padding: '2px 8px', borderRadius: 12 }}>{isZ ? <FaMinus size={6}/> : isP ? <FaArrowUp size={6}/> : <FaArrowDown size={6}/>} {isZ ? 'Same parking' : `${isP?'+':''}${v} parking`}</span>; })()}
                                {diff.land_size != null && diff.land_size !== 0 && (() => { const v = diff.land_size; const isP = v > 0; const c = isP ? '#16a34a' : '#dc2626'; const bg = isP ? '#f0fdf4' : '#fef2f2'; return <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 10, fontWeight: 500, color: c, backgroundColor: bg, padding: '2px 8px', borderRadius: 12 }}>{isP ? <FaArrowUp size={6}/> : <FaArrowDown size={6}/>} {`${isP?'+':''}${v}m² land`}</span>; })()}
                              </div>
                            )}

                            {/* Summary */}
                            {comp.summary && (
                              <p style={{ fontSize: 12, color: "#6b7280", lineHeight: 1.6, margin: 0, borderTop: "1px solid #f3f4f6", paddingTop: 10 }}>
                                {comp.summary}
                              </p>
                            )}
                          </div>

                          {/* Location comparison */}
                          {loc && (
                            <div style={{ borderTop: "1px solid #f0f0f0", padding: isMobile ? "12px 14px" : "14px 22px" }}>
                              {loc.map_url && (
                                <img src={loc.map_url} alt="Location comparison" style={{ width: "100%", height: 120, objectFit: "cover", borderRadius: 10, marginBottom: 10 }} />
                              )}
                              <div style={{ marginBottom: loc.proximity_comparison?.length > 0 ? 10 : 0 }}>
                                <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8, marginBottom: 4 }}>
                                  <div style={{ flex: 1, minWidth: 0 }}>
                                    {loc.relative_position && <div style={{ fontSize: 12, color: "#374151", fontWeight: 500, lineHeight: 1.5, marginBottom: 2 }}>{loc.relative_position}</div>}
                                    {loc.location_note && <div style={{ fontSize: 12, color: "#6b7280", lineHeight: 1.5 }}>{loc.location_note}</div>}
                                  </div>
                                  <span style={{ fontSize: 10, fontWeight: 600, color: (advStyles[loc.location_advantage] || advStyles.similar).color, whiteSpace: "nowrap", flexShrink: 0, marginTop: 2 }}>
                                    {(advStyles[loc.location_advantage] || advStyles.similar).label}
                                  </span>
                                </div>
                              </div>
                              {loc.proximity_comparison?.length > 0 && (
                                <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                                  {loc.proximity_comparison.map((pc, i) => (
                                    <div key={i} style={{ backgroundColor: "#f9fafb", borderRadius: 8, padding: "8px 10px", borderLeft: `3px solid ${pc.closer === 'subject' ? '#16a34a' : pc.closer === 'comparable' ? '#6b7280' : '#e5e7eb'}` }}>
                                      <div style={{ fontWeight: 600, color: "#1a1a1a", fontSize: 11, lineHeight: 1.4, marginBottom: 3 }}>{pc.landmark}</div>
                                      <div style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 10 }}>
                                        <span style={{ color: pc.closer === 'subject' ? '#16a34a' : '#6b7280', fontWeight: 500 }}>Subject: {pc.subject_distance}</span>
                                        <span style={{ color: "#d1d5db" }}>|</span>
                                        <span style={{ color: pc.closer === 'comparable' ? '#16a34a' : '#6b7280', fontWeight: 500 }}>Comp: {pc.comparable_distance}</span>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
                );
              })()}

              {/* Property notes — supports old string format and new array format */}
              {selectedReq.property_notes && (typeof selectedReq.property_notes === "string" || (Array.isArray(selectedReq.property_notes) && selectedReq.property_notes.length > 0)) && (() => {
                // Normalise: old string → single-item array, new format already an array
                const notesList = typeof selectedReq.property_notes === "string"
                  ? [{ id: "legacy", note: selectedReq.property_notes, created_at: null, source: "user" }]
                  : selectedReq.property_notes;
                const userNotes = notesList.filter(n => n.source !== "agent");
                const agentNotes = notesList.filter(n => n.source === "agent");
                const hasNotes = userNotes.length > 0 || agentNotes.length > 0;
                if (!hasNotes) return null;
                return (
                  <div style={{ padding: isMobile ? 16 : 24, borderBottom: selectedMeta.ai_summary ? "1px solid #e5e7eb" : "none" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                      <FaStickyNote style={{ fontSize: 13, color: "#f59e42" }} />
                      <h3 style={{ fontSize: 16, fontWeight: 700, color: "#1f2937", margin: 0 }}>Property Notes</h3>
                    </div>

                    {/* User / homeowner notes */}
                    {userNotes.length > 0 && (
                      <div style={{ marginBottom: agentNotes.length > 0 ? 16 : 0 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
                          <span style={{
                            fontSize: 10, fontWeight: 600, borderRadius: 10, padding: "1px 8px",
                            background: "#fef3c7", color: "#b45309", border: "1px solid #f59e42",
                          }}>Homeowner</span>
                        </div>
                        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                          {userNotes.map((n, i) => (
                            <div key={n.id || i} style={{
                              background: "#fffbeb", borderRadius: 8, padding: "12px 14px",
                              border: "1px solid #fef3c7", fontSize: 14, color: "#374151",
                              lineHeight: 1.6, whiteSpace: "pre-wrap",
                            }}>
                              {n.note}
                              {n.created_at && (
                                <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 6 }}>
                                  {formatDate(n.created_at)}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Agent notes */}
                    {agentNotes.length > 0 && (
                      <div>
                        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
                          <span style={{
                            fontSize: 10, fontWeight: 600, borderRadius: 10, padding: "1px 8px",
                            background: "#eff6ff", color: "#2563eb", border: "1px solid #bfdbfe",
                          }}>Agent</span>
                        </div>
                        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                          {agentNotes.map((n, i) => (
                            <div key={n.id || i} style={{
                              background: "#f0f5ff", borderRadius: 8, padding: "12px 14px",
                              border: "1px solid #dbeafe", fontSize: 14, color: "#374151",
                              lineHeight: 1.6, whiteSpace: "pre-wrap",
                            }}>
                              {n.note}
                              {n.created_at && (
                                <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 6 }}>
                                  {formatDate(n.created_at)}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* AI Summary for selected entry */}
              {selectedMeta.ai_summary && (
                <div style={{ padding: isMobile ? 16 : 24, borderBottom: selectedMeta.history?.length ? "1px solid #e5e7eb" : "none" }}>
                  <h3 style={{ fontSize: 16, fontWeight: 700, color: "#1f2937", margin: "0 0 12px 0" }}>AI Market Summary</h3>
                  <TruncatedAISummary html={selectedMeta.ai_summary} />
                </div>
              )}

              {/* Property History */}
              {selectedMeta.history && Array.isArray(selectedMeta.history) && selectedMeta.history.length > 0 && (
                <div style={{ padding: isMobile ? 16 : 24 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
                    <FaHistory style={{ fontSize: 13, color: "#6b7280" }} />
                    <h3 style={{ fontSize: 16, fontWeight: 700, color: "#1f2937", margin: 0 }}>Property History</h3>
                  </div>
                  <div style={{ position: "relative", paddingLeft: 20 }}>
                    {/* Timeline line */}
                    <div style={{ position: "absolute", left: 5, top: 4, bottom: 4, width: 2, background: "#e5e7eb", borderRadius: 1 }} />
                    {selectedMeta.history.map((event, i) => {
                      const isSale = event.type === 'Sale';
                      const dotColor = isSale ? "#2563eb" : "#8b5cf6";
                      const badgeBg = isSale ? "#eff6ff" : "#f5f3ff";
                      const badgeColor = isSale ? "#2563eb" : "#7c3aed";
                      const badgeBorder = isSale ? "#bfdbfe" : "#ddd6fe";
                      const dateStr = event.date ? new Date(event.date).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : 'Unknown date';
                      const priceStr = event.price != null && event.price !== 0
                        ? `$${event.price.toLocaleString()}`
                        : null;
                      return (
                        <div key={i} style={{ position: "relative", paddingBottom: i < selectedMeta.history.length - 1 ? 16 : 0, marginBottom: i < selectedMeta.history.length - 1 ? 4 : 0 }}>
                          {/* Timeline dot */}
                          <div style={{ position: "absolute", left: -18, top: 3, width: 10, height: 10, borderRadius: "50%", background: dotColor, border: "2px solid #fff", boxShadow: "0 0 0 2px " + dotColor + "40" }} />
                          <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
                            <span style={{ fontSize: 10, fontWeight: 600, color: badgeColor, background: badgeBg, border: `1px solid ${badgeBorder}`, borderRadius: 50, padding: "2px 10px", letterSpacing: "0.03em" }}>
                              {event.type || "Unknown"}
                            </span>
                            {priceStr && (
                              <span style={{ fontSize: 15, fontWeight: 700, color: "#1f2937" }}>
                                {priceStr}
                                {event.label && <span style={{ fontSize: 11, color: "#9ca3af", fontWeight: 400, marginLeft: 4 }}>{event.label}</span>}
                              </span>
                            )}
                            {!priceStr && <span style={{ fontSize: 11, color: "#9ca3af", fontWeight: 400 }}>Price unknown</span>}
                          </div>
                          <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 2 }}>{dateStr}</div>
                          {event.days_on_market != null && (
                            <div style={{ fontSize: 11, color: "#6b7280", marginTop: 1 }}>{event.days_on_market} days on market</div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Empty state when selected entry has neither */}
              {selectedMeta.valuation == null && selectedMeta.rental_estimate == null && !(selectedReq.property_notes?.length) && !selectedMeta.ai_summary && !selectedMeta.history?.length && (
                <div style={{ padding: isMobile ? 16 : 24, textAlign: "center", color: "#9ca3af", fontSize: 13 }}>
                  No estimates or AI analysis available for this entry.
                </div>
              )}
            </div>

          </>
        )}
      </div>
    </div>
  );
}
